import { forwardRef } from 'react';
import { Box, BoxProps } from './box';

/**
 * FlexBetween component - A convenience wrapper for space-between layouts.
 *
 * Pre-configured with `display="flex"`, `justifyContent="space-between"`, and `alignItems="center"`.
 * The header pattern used in ~15% of Box usage (Widget, Sidebar, MapActionPanel).
 *
 * @example
 * // Header with title and actions
 * <FlexBetween p={2}>
 *   <Typography variant="h6">Widget Title</Typography>
 *   <IconButton><CloseIcon /></IconButton>
 * </FlexBetween>
 *
 * @example
 * // List item with label and value
 * <FlexBetween>
 *   <Typography color="text.secondary">Label</Typography>
 *   <Typography fontWeight="bold">Value</Typography>
 * </FlexBetween>
 *
 * @example
 * // With custom alignment
 * <FlexBetween alignItems="flex-start" p={2}>
 *   <Box>Left content</Box>
 *   <Box>Right content</Box>
 * </FlexBetween>
 */
export interface FlexBetweenProps extends BoxProps {
  /**
   * Alignment of items on the cross axis.
   * @default 'center'
   */
  alignItems?: BoxProps['alignItems'];
}

export const FlexBetween = forwardRef<HTMLDivElement, FlexBetweenProps>(
  ({ alignItems = 'center', ...props }, ref) => (
    <Box
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      display="flex"
      justifyContent="space-between"
      alignItems={alignItems}
      {...props}
    />
  )
);

FlexBetween.displayName = 'FlexBetween';
